import PropTypes from "prop-types";
import { Package, RefreshCw, AlertTriangle, Clock, CheckCircle2, XCircle, Archive } from "lucide-react";
import StatusBadge from "./StatusBadge.jsx";

/**
 * DigitalPackageStatus — estado del paquete comercial digital (MVP-04B).
 *
 * Hermano de `DigitalProductStatus` (04A), no una variante suya: aquí el eje de
 * estado es la generación/revisión del ZIP (3MF + PDF + plantillas), no el
 * precio. Presentacional y reutilizable por propietario y administración.
 * NUNCA incluye un CTA de compra ni de descarga: "paquete aprobado" es todavía
 * un estado técnico, no un derecho de descarga.
 *
 * Estados: empty | loading | error | generando | generado | invalido |
 * pendiente_revision | aprobado | rechazado | obsoleto.
 */

const STATE_META = {
    generando: { tone: "info", icon: Clock, label: "Generando" },
    generado: { tone: "info", icon: Clock, label: "Generado" },
    pendiente_revision: { tone: "warning", icon: Clock, label: "Pendiente de revisión" },
    aprobado: { tone: "success", icon: CheckCircle2, label: "Aprobado" },
    rechazado: { tone: "error", icon: XCircle, label: "Rechazado" },
    obsoleto: { tone: "neutral", icon: Archive, label: "Obsoleto" },
    invalido: { tone: "error", icon: AlertTriangle, label: "No válido" },
};

export default function DigitalPackageStatus({
    state = "empty",
    hasTemplate = false,
    lastError = "",
    rejectionReason = "",
    busy = false,
    onRetry = null,
    className = "",
}) {
    const btn = (label, handler, tone = "neutral") =>
        handler ? (
            <button
                type="button"
                onClick={handler}
                disabled={busy}
                aria-busy={busy}
                className={
                    "mt-2 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium " +
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 " +
                    "focus-visible:ring-offset-base-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed " +
                    (tone === "error"
                        ? "border-error/50 text-error hover:bg-error/10"
                        : "border-base-content/30 text-base-content hover:bg-base-content/10")
                }
            >
                <RefreshCw className={`w-4 h-4 ${busy ? "motion-safe:animate-spin" : ""}`} aria-hidden /> {label}
            </button>
        ) : null;

    const Header = () => (
        <div className="flex items-center gap-2 text-sm font-semibold text-base-content">
            <Package className="w-4 h-4 shrink-0 text-base-content/70" aria-hidden /> Paquete comercial
        </div>
    );

    if (state === "empty") {
        return (
            <div className={`rounded-xl border border-base-content/15 p-4 ${className}`}>
                <Header />
                <p className="mt-1 text-xs text-base-content/60">
                    Todavía no se ha generado ningún paquete comercial para este diseño.
                </p>
            </div>
        );
    }

    if (state === "loading" || state === "generando") {
        return (
            <div className={`rounded-xl border border-base-content/15 p-4 ${className}`} aria-live="polite">
                <Header />
                <div className="mt-2 h-4 w-40 rounded bg-base-content/15 motion-safe:animate-pulse"
                     aria-label="Generando el paquete comercial" />
                <p className="mt-2 text-xs text-base-content/50">Generando 3MF, PDF y plantillas…</p>
            </div>
        );
    }

    if (state === "error") {
        return (
            <div role="alert" className={`rounded-xl border border-error/40 bg-error/10 p-4 ${className}`}>
                <Header />
                <p className="mt-1 text-xs text-base-content/70">
                    No se pudo consultar el estado del paquete ahora mismo.
                </p>
                {btn("Reintentar", onRetry, "error")}
            </div>
        );
    }

    const meta = STATE_META[state] || STATE_META.invalido;
    const Icon = meta.icon;

    return (
        <div className={`rounded-xl border border-base-content/15 p-4 ${className}`}>
            <div className="flex items-center justify-between gap-2">
                <Header />
                <StatusBadge label={meta.label} tone={meta.tone} size="sm" outline
                             icon={<Icon className="w-3.5 h-3.5" aria-hidden />} />
            </div>

            {state === "invalido" && (
                <p className="mt-2 text-xs text-error">
                    El paquete no se pudo generar{lastError ? `: ${lastError}` : "."} Contacta con
                    administración si persiste.
                </p>
            )}
            {state === "rechazado" && (
                <p className="mt-2 text-xs text-base-content/70">
                    Revisión rechazada{rejectionReason ? `: ${rejectionReason}` : "."}
                </p>
            )}
            {state === "obsoleto" && (
                <p className="mt-2 text-xs text-base-content/60">
                    Este paquete quedó obsoleto tras un cambio en el diseño de origen.
                </p>
            )}
            {(state === "generado" || state === "pendiente_revision") && (
                <p className="mt-2 text-xs text-base-content/60">
                    {hasTemplate
                        ? "Incluye plantilla de montaje. Pendiente de revisión administrativa."
                        : "Pendiente de revisión administrativa."}
                </p>
            )}

            {/* Nunca CTA de compra/descarga: "aprobado" aquí es un estado técnico. */}
            <p className="mt-3 text-[11px] leading-relaxed text-base-content/50">
                {state === "aprobado"
                    ? "Paquete aprobado técnicamente. Todavía no equivale a un derecho de descarga ni a una compra."
                    : "Estado interno de generación y revisión. No disponible para compra ni descarga."}
            </p>
        </div>
    );
}

DigitalPackageStatus.propTypes = {
    state: PropTypes.oneOf([
        "empty", "loading", "error",
        "generando", "generado", "invalido", "pendiente_revision", "aprobado", "rechazado", "obsoleto",
    ]),
    hasTemplate: PropTypes.bool,
    lastError: PropTypes.string,
    rejectionReason: PropTypes.string,
    busy: PropTypes.bool,
    onRetry: PropTypes.func,
    className: PropTypes.string,
};
